import { Controller } from '@hotwired/stimulus';
import { post } from '@rails/request.js';

// Debug console for the upcoming Acture Fit API. Vends a server-signed
// PASETO token into an editable field, calls the configured address directly
// from the browser with that token, and logs the full exchange for
// inspection. Editing the token produces invalid tokens on purpose, to debug
// how the API rejects them.
export default class extends Controller {
  static targets = [
    'identifier',
    'token',
    'tokenError',
    'expiry',
    'method',
    'url',
    'log',
    'logEmpty',
    'entryTemplate',
  ];

  static values = {
    tokenUrl: String,
    corsHint: String,
    undecodableHint: String,
    tokenError: String,
  };

  #expiryTimer;
  #expiresAt = null;

  connect() {
    this.refreshToken();
    this.#expiryTimer = setInterval(() => this.#renderExpiry(), 1000);
  }

  disconnect() {
    clearInterval(this.#expiryTimer);
  }

  async refreshToken() {
    this.tokenTarget.value = await this.#signToken();
    this.tokenChanged();
  }

  // Tracks the expiry of whatever is in the token field, including manual
  // edits — the countdown always reflects the token that would be sent.
  tokenChanged() {
    const exp = this.#decodeToken(this.#token)?.claims?.exp;
    this.#expiresAt = exp ? Date.parse(exp) : null;
    this.#renderExpiry();
  }

  sendRequest() {
    this.#request(this.urlTarget.value);
  }

  clearLog() {
    this.logTarget.replaceChildren();
    this.logEmptyTarget.hidden = false;
  }

  get #token() {
    return this.tokenTarget.value.trim();
  }

  async #request(url) {
    if (!url) return;

    const method = this.methodTarget.value || 'GET';
    const entry = this.#prependEntry(method, url);
    const token = this.#token;

    const headers = { 'X-Request-Id': crypto.randomUUID() };
    if (token) headers.Authorization = `Bearer ${token}`;

    entry.request.textContent = [
      `${method} ${url}`,
      ...Object.entries(headers).map(([name, value]) => `${name}: ${value}`),
    ].join('\n');

    const decoded = this.#decodeToken(token);
    entry.token.textContent = decoded
      ? JSON.stringify(decoded, null, 2)
      : this.undecodableHintValue;

    try {
      const startedAt = performance.now();
      const response = await fetch(url, { method, headers });
      const duration = Math.round(performance.now() - startedAt);
      const body = await response.text();

      entry.status.textContent =
        `${response.status} ${response.statusText}`.trim();
      entry.status.dataset.state = response.ok ? 'success' : 'error';
      entry.response.textContent = this.#formatResponse(
        response,
        body,
        duration,
      );
    } catch (error) {
      entry.status.textContent = error.name || 'Error';
      entry.status.dataset.state = 'error';

      // fetch() rejects with a TypeError when the server is unreachable or
      // the response is blocked by CORS.
      const hint =
        error instanceof TypeError ? `\n\n${this.corsHintValue}` : '';
      entry.response.textContent = `${error}${hint}`;
    }
  }

  async #signToken() {
    this.#clearTokenError();

    try {
      const response = await post(this.tokenUrlValue, {
        body: JSON.stringify({ identifier: this.identifierTarget.value }),
        responseKind: 'json',
      });
      if (!response.ok) {
        this.#showTokenError(`HTTP ${response.statusCode}`);
        return '';
      }

      return (await response.json).token || '';
    } catch (error) {
      this.#showTokenError(`${error}`);
      return '';
    }
  }

  #showTokenError(detail) {
    this.tokenErrorTarget.textContent = `${this.tokenErrorValue} (${detail})`;
    this.tokenErrorTarget.hidden = false;
  }

  #clearTokenError() {
    this.tokenErrorTarget.hidden = true;
    this.tokenErrorTarget.textContent = '';
  }

  #renderExpiry() {
    if (!this.#expiresAt || Number.isNaN(this.#expiresAt)) {
      this.expiryTarget.textContent = '–';
      delete this.expiryTarget.dataset.state;
      return;
    }

    const remaining = Math.max(
      0,
      Math.round((this.#expiresAt - Date.now()) / 1000),
    );
    const seconds = `${remaining % 60}`.padStart(2, '0');
    this.expiryTarget.textContent = `${Math.floor(remaining / 60)}:${seconds}`;

    if (remaining === 0) {
      this.expiryTarget.dataset.state = 'expired';
    } else {
      delete this.expiryTarget.dataset.state;
    }
  }

  #decodeToken(token) {
    try {
      const [version, purpose, payload, footer] = token.split('.');
      if (version !== 'v4' || purpose !== 'public' || !payload) return null;

      const bytes = this.#base64urlDecode(payload);
      const claims = JSON.parse(new TextDecoder().decode(bytes.slice(0, -64)));
      const decodedFooter = footer
        ? JSON.parse(new TextDecoder().decode(this.#base64urlDecode(footer)))
        : null;

      return { claims, footer: decodedFooter };
    } catch {
      return null;
    }
  }

  #base64urlDecode(encoded) {
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    return Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
  }

  // Collects the log entry's elements keyed by their `data-ref` name, so the
  // request lifecycle can fill them in as it progresses.
  #prependEntry(method, url) {
    const item =
      this.entryTemplateTarget.content.firstElementChild.cloneNode(true);
    const entry = {};
    for (const element of item.querySelectorAll('[data-ref]')) {
      entry[element.dataset.ref] = element;
    }

    entry.method.textContent = method;
    entry.url.textContent = url;
    this.logTarget.prepend(item);
    this.logEmptyTarget.hidden = true;

    return entry;
  }

  #formatResponse(response, body, duration) {
    const statusLine =
      `HTTP ${response.status} ${response.statusText} (${duration} ms)`.trim();
    // Cross-origin responses only expose CORS-safelisted headers, plus any
    // the server lists in Access-Control-Expose-Headers.
    const headers = [...response.headers.entries()]
      .map(([name, value]) => `${name}: ${value}`)
      .join('\n');
    const head = headers ? `${statusLine}\n${headers}` : statusLine;

    return `${head}\n\n${this.#prettify(body)}`;
  }

  #prettify(body) {
    try {
      return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      return body;
    }
  }
}
